/**
 * CreatorDashboard - Main dashboard for Creator Impact Portal
 * 
 * Shows:
 * - Global impact summary stats
 * - Grid of artifact metric cards
 * - Domain impact summary (Phase 5)
 * - Admin panel for managing creators (admin only)
 * - All artifacts table with filtering/sorting (admin only)
 * - View As mode for admins
 * - Global network totals (admin only)
 * - Admin signal debug section (Phase 5)
 */

import { useState } from "react";
import { useCreatorAccount, useCreatorArtifacts, useCreatorSummaryStats, useCreatorDomainStats } from "@/hooks/useCreatorAnalytics";
import { useIsCreatorAdmin, useAllCreatorAccounts, useAdminNetworkSummaryStats } from "@/hooks/useCreatorAdminAnalytics";
import { CreatorSummaryStats, CompletionsSummary } from "@/components/creator/CreatorSummaryStats";
import { ArtifactMetricCard } from "@/components/creator/ArtifactMetricCard";
import { CreatorAdminPanel } from "@/components/creator/CreatorAdminPanel";
import { CreatorArtifactsTable } from "@/components/creator/CreatorArtifactsTable";
import { AdminArtifactsTable } from "@/components/creator/AdminArtifactsTable";
import { ProvidersSection } from "@/components/creator/ProvidersSection";
import { ViewAsBanner } from "@/components/creator/ViewAsBanner";
import { DomainImpactSummary } from "@/components/creator/DomainImpactSummary";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Loader2, LogOut, BarChart3, BookOpen, RefreshCw, Shield, Users, FileText, ChevronDown, Bug } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { CreatorSubmissionsPanel, SubmissionReviewQueue } from "@/components/creator/CreatorSubmissionsPanel";

const CreatorDashboard = () => {
  const navigate = useNavigate();
  // FIX-R33-LOW-3: route signOut through AuthContext so every
  // identity-linked localStorage key (activeShopId, chronicle context,
  // joinTeamToken, tutorial flag) is scrubbed consistently.
  const { signOut } = useAuth();
  const { toast } = useToast();
  const { data: account, isLoading: accountLoading } = useCreatorAccount();
  const { data: isAdmin, isLoading: adminLoading } = useIsCreatorAdmin();
  const { data: allCreators } = useAllCreatorAccounts();
  
  // View As state
  const [viewingAsCreatorId, setViewingAsCreatorId] = useState<string | null>(null);
  const [viewingAsCreatorName, setViewingAsCreatorName] = useState<string>("");
  
  // Admin tab state
  const [adminTab, setAdminTab] = useState<string>("overview");
  
  // Get artifacts - use override if viewing as another creator
  const { data: artifacts, isLoading: artifactsLoading, refetch } = useCreatorArtifacts(
    viewingAsCreatorId || undefined
  );
  const creatorSummaryStats = useCreatorSummaryStats(viewingAsCreatorId || undefined);
  const domainStats = useCreatorDomainStats(viewingAsCreatorId || undefined);
  
  // Admin-specific: fetch network-wide stats for Overview tab
  const { data: adminNetworkStats } = useAdminNetworkSummaryStats();
  
  // Use admin network stats when admin is on Overview, otherwise use creator-specific stats
  const summaryStats = (isAdmin && !viewingAsCreatorId && adminNetworkStats) 
    ? adminNetworkStats 
    : creatorSummaryStats;

  const isLoading = accountLoading || artifactsLoading || adminLoading;

  const handleSignOut = async () => {
    await signOut();
    navigate("/creator/auth");
  };

  // Core-100 R4 W2 (CORE-S): "In creator admin view-as, the real actor stays recorded." The server writes a durable
  // record (record_creator_view_as: the real admin, the creator, enter/exit, when) that the creator can read. Entering
  // view-as is refused if the record cannot be written — fail closed; the browser buffer no longer stands in for it.
  const handleViewAs = async (creatorId: string, creatorName: string) => {
    const { error } = await supabase.rpc('record_creator_view_as' as never, { p_creator_id: creatorId, p_action: 'enter' } as never);
    if (error) {
      toast({ title: 'View as not started', description: 'The view could not be recorded, so it was not opened.', variant: 'destructive' });
      return;
    }
    setViewingAsCreatorId(creatorId);
    setViewingAsCreatorName(creatorName);
  };

  const handleExitViewAs = async () => {
    const target = viewingAsCreatorId;
    setViewingAsCreatorId(null);
    setViewingAsCreatorName("");
    if (target) {
      const { error } = await supabase.rpc('record_creator_view_as' as never, { p_creator_id: target, p_action: 'exit' } as never);
      if (error) console.warn('[creator-admin] view-as exit not recorded:', error.message);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* View As Banner */}
      {viewingAsCreatorId && (
        <ViewAsBanner creatorName={viewingAsCreatorName} onExit={handleExitViewAs} />
      )}
      
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <BarChart3 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-semibold">Creator Impact Portal</h1>
                {isAdmin && (
                  <Badge variant="secondary" className="text-xs">
                    <Shield className="h-3 w-3 mr-1" />
                    Admin
                  </Badge>
                )}
              </div>
              {account?.name && (
                <p className="text-sm text-muted-foreground">{account.name}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-1" />
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={handleSignOut}>
              <LogOut className="h-4 w-4 mr-1" />
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 space-y-8">
        {/* Admin Section - Only visible to admins and not in View As mode */}
        {isAdmin && !viewingAsCreatorId && (
          <section className="space-y-4">
            <Tabs value={adminTab} onValueChange={setAdminTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3 max-w-md">
                <TabsTrigger value="overview" className="flex items-center gap-1">
                  <BarChart3 className="h-3 w-3" />
                  Overview
                </TabsTrigger>
                <TabsTrigger value="creators" className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  Creators
                </TabsTrigger>
                <TabsTrigger value="artifacts" className="flex items-center gap-1">
                  <FileText className="h-3 w-3" />
                  Artifacts
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="overview" className="mt-4 space-y-6">
                {/* Summary Stats for Admin */}
                <div>
                  <h2 className="text-lg font-semibold mb-4">Network Impact Summary</h2>
                  <CreatorSummaryStats stats={summaryStats} />
                  <div className="mt-4">
                    <CompletionsSummary 
                      verified={summaryStats.verifiedCompletions} 
                      likely={summaryStats.likelyCompletions} 
                    />
                  </div>
                </div>

                {/* Domain Impact Summary */}
                {domainStats.length > 0 && (
                  <DomainImpactSummary domains={domainStats} />
                )}

                {/* Admin Signal Debug Section */}
                <AdminSignalDebug artifacts={artifacts} />
              </TabsContent>
              
              <TabsContent value="creators" className="mt-4 space-y-6">
                <SubmissionReviewQueue enabled={!!isAdmin} />
                <ProvidersSection />
                <CreatorAdminPanel onViewAs={handleViewAs} />
              </TabsContent>
              
              <TabsContent value="artifacts" className="mt-4">
                <AdminArtifactsTable />
              </TabsContent>
            </Tabs>
          </section>
        )}

        {/* Non-Admin View OR View As Mode */}
        {(!isAdmin || viewingAsCreatorId) && (
          <>
            {/* Summary Stats */}
            <section>
              <h2 className="text-lg font-semibold mb-4">Global Impact Summary</h2>
              <CreatorSummaryStats stats={summaryStats} />
              
              {/* Completions row */}
              <div className="mt-4">
                <CompletionsSummary 
                  verified={summaryStats.verifiedCompletions} 
                  likely={summaryStats.likelyCompletions} 
                />
              </div>
            </section>

            {/* Domain Impact Summary */}
            {domainStats.length > 0 && (
              <section>
                <DomainImpactSummary domains={domainStats} />
              </section>
            )}

            {/* Core-100 R4 W2 (CORE-S): the creator's own authoring and submission (never shown in view-as) */}
            {!viewingAsCreatorId && (
              <section>
                <CreatorSubmissionsPanel creatorId={account?.id} />
              </section>
            )}

            {/* Artifacts Table */}
            <section>
              {!artifacts || artifacts.length === 0 ? (
                <Card>
                  <CardHeader className="text-center">
                    <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                      <BookOpen className="h-6 w-6 text-muted-foreground" />
                    </div>
                    <CardTitle>No Artifacts Linked</CardTitle>
                    <CardDescription>
                      {viewingAsCreatorId 
                        ? "This creator doesn't have any training content linked yet."
                        : "Your training content hasn't been linked to this account yet."}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="text-center">
                    <p className="text-sm text-muted-foreground">
                      {isAdmin 
                        ? "Use the admin panel to link training artifacts to creator accounts."
                        : "Please contact support to link your training artifacts to your creator account."}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <CreatorArtifactsTable 
                  artifacts={artifacts} 
                  creatorName={viewingAsCreatorId ? viewingAsCreatorName : undefined}
                />
              )}
            </section>
          </>
        )}

        {/* Footer info */}
        <section className="pt-8 border-t">
          <div className="text-center text-sm text-muted-foreground space-y-1">
            <p>
              Metrics are aggregated and anonymized across the OptiBay network.
            </p>
            <p>
              Data is only shown when sufficient sample sizes are met (3+ shops, 5+ technicians).
            </p>
          </div>
        </section>
      </main>
    </div>
  );
};

// =============================================
// Admin Signal Debug Section (Phase 5)
// =============================================

import type { CreatorArtifact } from "@/hooks/useCreatorAnalytics";

function AdminSignalDebug({ artifacts }: { artifacts: CreatorArtifact[] | undefined }) {
  if (!artifacts || artifacts.length === 0) return null;

  const artifactsWithMetrics = artifacts.filter(a => a.metrics);
  if (artifactsWithMetrics.length === 0) return null;

  return (
    <Collapsible>
      <CollapsibleTrigger className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors group w-full p-3 rounded-lg bg-muted/30 hover:bg-muted/40">
        <Bug className="h-4 w-4" />
        <span className="font-medium">Signal Debug</span>
        <ChevronDown className="h-4 w-4 ml-auto transition-transform group-data-[state=open]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-2">
        <Card>
          <CardContent className="pt-4">
            <div className="space-y-3 text-xs font-mono">
              {artifactsWithMetrics.map(a => {
                const m = a.metrics!;
                return (
                  <div key={a.id} className="p-2 rounded bg-muted/40 space-y-1">
                    <div className="font-semibold text-foreground">{a.artifact?.title}</div>
                    <div className="text-muted-foreground">
                      opens: {m.total_opens ?? 'suppressed'} | unique_opens: {m.unique_technician_opens ?? 'suppressed'} | sessions: {m.total_sessions}
                    </div>
                    <div className="text-muted-foreground">
                      notes_rate: {Number(m.behavioral_notes_rate).toFixed(4)} | progress_rate: {Number(m.behavioral_progress_rate).toFixed(4)} | additional_rate: {Number(m.behavioral_additional_training_rate).toFixed(4)}
                    </div>
                    <div className="text-muted-foreground">
                      pensieve_score: {Number(m.avg_pensieve_score).toFixed(2)} | pensieve_n: {m.pensieve_sample_count} | domain: {m.primary_domain ?? 'suppressed'}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </CollapsibleContent>
    </Collapsible>
  );
}

export default CreatorDashboard;
